// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-4 W5 (slice 1): Team performance — supervisors and managers manage
 * response time and workload here. Per-agent FRT/TTR medians, load, breaches;
 * per-customer rollups. Window selector 7/30/90 days. QBR export lands with
 * the full W5 build.
 */
import { useEffect, useMemo, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: what each column shows, for its header funnel. Agent keys are the sort keys; customer
// keys are the lower-cased header labels.
const AGENT_FUNNELS: FunnelColumn<any>[] = [
  { key: 'name',               label: 'Agent',                value: a => a.name },
  { key: 'open_now',           label: 'Open now',             value: a => a.open_now },
  { key: 'critical_open',      label: 'Critical open',        value: a => a.critical_open },
  { key: 'resolved_in_window', label: 'Resolved',             value: a => a.resolved_in_window },
  { key: 'frt_median_min',     label: 'First response (med)', value: a => fmtMin(a.frt_median_min) },
  { key: 'ttr_median_min',     label: 'Resolution (med)',     value: a => fmtMin(a.ttr_median_min) },
  { key: 'breached',           label: 'Breaches',             value: a => a.breached },
]
const CUSTOMER_FUNNELS: FunnelColumn<any>[] = [
  { key: 'customer',         label: 'Customer',         value: c => c.name },
  { key: 'open now',         label: 'Open now',         value: c => c.open_now },
  { key: 'created',          label: 'Created',          value: c => c.created_in_window },
  { key: 'resolution (med)', label: 'Resolution (med)', value: c => fmtMin(c.ttr_median_min) },
  { key: 'breaches',         label: 'Breaches',         value: c => c.breached },
]

export default function SupportTeamReportPage() {
  const [data, setData] = useState<api.SupportTeamReport | null>(null)
  const [days, setDays] = useState(30)
  const [error, setError] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<string>('open_now')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    setData(null)
    api.getSupportTeamReport(days).then(setData).catch(e => setError(friendlyError(e)))
  }, [days])

  const agents = useMemo(() => {
    if (!data) return []
    const arr = [...data.agents]
    arr.sort((a: any, b: any) => {
      const av = a[sortKey] ?? -1, bv = b[sortKey] ?? -1
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [data, sortKey, sortDir])

  // The header funnels filter the whole (sorted) list of each table.
  const agentFx = useTableFunnels<any>(agents, AGENT_FUNNELS)
  const customerFx = useTableFunnels<any>(data?.customers, CUSTOMER_FUNNELS)

  const clickSort = (k: string) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir('desc') }
  }

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><BarChart3 size={21} /> Team performance</h1>
          <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
            Response and resolution medians, load, and SLA breaches — per agent and per customer.
          </div>
        </div>
        <SearchableSelect value={String(days)} onChange={v => setDays(Number(v))} ariaLabel="Report window"
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
          ]} style={{ minWidth: 150 }} />
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      {!data ? <LoadingState label="Computing…" /> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi label="Created" value={data.summary.created} />
            <Kpi label="Resolved" value={data.summary.resolved} />
            <Kpi label="Open now" value={data.summary.open_now} />
            <Kpi label="Unassigned open" value={data.summary.unassigned_open} tone={data.summary.unassigned_open > 0 ? 'warn' : undefined} />
            <Kpi label="First response (median)" value={fmtMin(data.summary.frt_median_min)} />
            <Kpi label="Resolution (median)" value={fmtMin(data.summary.ttr_median_min)} />
            <Kpi label="SLA breaches" value={data.summary.breached} tone={data.summary.breached > 0 ? 'bad' : undefined} />
          </div>

          <h2 style={h2}>Agents</h2>
          <div style={cardStyle}>
            {agents.length === 0 ? <div style={emptyStyle}>No assigned tickets in this window.</div> :
            <>
            {agentFx.chips}
            <table {...agentFx.tableProps} style={tableStyle}>
              <thead><tr>
                {[['name', 'Agent'], ['open_now', 'Open now'], ['critical_open', 'Critical open'], ['resolved_in_window', 'Resolved'], ['frt_median_min', 'First response (med)'], ['ttr_median_min', 'Resolution (med)'], ['breached', 'Breaches']].map(([k, l]) => (
                  <th key={k} onClick={() => clickSort(k)} style={thStyle}>{l}{sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''} {agentFx.funnel(k)}</th>
                ))}
              </tr></thead>
              <tbody>
                {agentFx.rows.map((a: any) => (
                  <tr key={a.account_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{a.name}</td>
                    <td style={tdStyle}>{a.open_now}</td>
                    <td style={{ ...tdStyle, color: a.critical_open > 0 ? '#b91c1c' : 'inherit', fontWeight: a.critical_open > 0 ? 700 : 400 }}>{a.critical_open}</td>
                    <td style={tdStyle}>{a.resolved_in_window}</td>
                    <td style={tdStyle}>{fmtMin(a.frt_median_min)}</td>
                    <td style={tdStyle}>{fmtMin(a.ttr_median_min)}</td>
                    <td style={{ ...tdStyle, color: a.breached > 0 ? '#b91c1c' : 'inherit' }}>{a.breached}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>}
          </div>

          <h2 style={h2}>Customers</h2>
          {customerFx.chips}
          <div style={cardStyle}>
            <table {...customerFx.tableProps} style={tableStyle}>
              <thead><tr>
                {['Customer', 'Open now', 'Created', 'Resolution (med)', 'Breaches'].map(l => <th key={l} style={{ ...thStyle, cursor: 'default' }}>{l} {customerFx.funnel(l.toLowerCase())}</th>)}
              </tr></thead>
              <tbody>
                {customerFx.rows.map((c: any) => (
                  <tr key={c.pharmacy_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                    <td style={tdStyle}>{c.open_now}</td>
                    <td style={tdStyle}>{c.created_in_window}</td>
                    <td style={tdStyle}>{fmtMin(c.ttr_median_min)}</td>
                    <td style={{ ...tdStyle, color: c.breached > 0 ? '#b91c1c' : 'inherit' }}>{c.breached}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 10 }}>
            Medians are raw clock time from ticket creation. "Breaches" is the SLA sweep's flag. Business-hours-adjusted
            attainment % and the exportable QBR pack land with the full W5 reporting build (D-SUPPORT-3 §6).
          </div>
        </>
      )}
    </div>
  )
}

function fmtMin(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—'
  if (m < 60) return `${Math.round(m)}m`
  if (m < 60 * 24) return `${(m / 60).toFixed(1)}h`
  return `${(m / 60 / 24).toFixed(1)}d`
}

function Kpi({ label, value, tone }: { label: string; value: string | number; tone?: 'warn' | 'bad' }) {
  const color = tone === 'bad' ? '#b91c1c' : tone === 'warn' ? '#b45309' : 'var(--color-text)'
  return (
    <div style={{ flex: '1 1 140px', minWidth: 120, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
    </div>
  )
}

const h2: React.CSSProperties = { fontSize: 15, fontWeight: 700, margin: '18px 0 8px' }
const cardStyle: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 8, overflowX: 'auto' }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 720 }
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }
const tdStyle: React.CSSProperties = { padding: '10px 10px' }
const emptyStyle: React.CSSProperties = { padding: '18px', color: 'var(--color-text-mute)', fontSize: 13.5 }
